type AuthDebugDetails = Record<string, unknown>;
type AuthDebugLevel = "debug" | "warn";

const AUTH_DEBUG_LOG_KEY = "terramatrix_auth_debug_log";
const AUTH_DEBUG_LOG_LIMIT = 200;

interface AuthDebugEntry {
  at: string;
  level: AuthDebugLevel;
  event: string;
  details: AuthDebugDetails;
}

function isObjectLike(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readObjectProperty(source: Record<string, unknown>, key: string): Record<string, unknown> | null {
  const value = Reflect.get(source, key);
  return isObjectLike(value) ? value : null;
}

function readStringProperty(source: Record<string, unknown>, key: string): string | undefined {
  const value = Reflect.get(source, key);
  return typeof value === "string" ? value : undefined;
}

function readNumberProperty(source: Record<string, unknown>, key: string): number | undefined {
  const value = Reflect.get(source, key);
  return typeof value === "number" ? value : undefined;
}

export function isAuthDebugEnabled(): boolean {
  if (import.meta.env.VITE_AUTH_DEBUG === "true") {
    return true;
  }

  if (typeof window === "undefined") {
    return false;
  }

  try {
    return window.localStorage.getItem("terramatrix_auth_debug") === "true";
  } catch {
    return false;
  }
}

function readAuthDebugLog(): AuthDebugEntry[] {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(AUTH_DEBUG_LOG_KEY);
    if (!raw) {
      return [];
    }

    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((entry): entry is AuthDebugEntry => isObjectLike(entry))
      : [];
  } catch {
    return [];
  }
}

function writeAuthDebugEntry(entry: AuthDebugEntry): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const entries = [...readAuthDebugLog(), entry].slice(-AUTH_DEBUG_LOG_LIMIT);
    window.localStorage.setItem(AUTH_DEBUG_LOG_KEY, JSON.stringify(entries));
  } catch {
    // Ignore storage failures; console output still covers the live page.
  }
}

function writeAndPrintAuthDebug(
  level: AuthDebugLevel,
  event: string,
  details: AuthDebugDetails,
): void {
  if (!isAuthDebugEnabled()) {
    return;
  }

  const entry = {
    at: new Date().toISOString(),
    level,
    event,
    details,
  };
  writeAuthDebugEntry(entry);

  if (level === "warn") {
    console.warn("[auth-debug]", event, {
      at: entry.at,
      ...details,
    });
    return;
  }

  console.debug("[auth-debug]", event, {
    at: entry.at,
    ...details,
  });
}

export function describeAuthError(error: unknown): AuthDebugDetails {
  if (!isObjectLike(error)) {
    return { errorType: typeof error };
  }

  const response = readObjectProperty(error, "response");
  const config = readObjectProperty(error, "config");
  const data = response ? readObjectProperty(response, "data") : null;

  return {
    name: readStringProperty(error, "name"),
    message: readStringProperty(error, "message"),
    code: readStringProperty(error, "code"),
    status: response ? readNumberProperty(response, "status") : undefined,
    responseCode: data ? readStringProperty(data, "code") : undefined,
    responseMessage: data ? readStringProperty(data, "message") : undefined,
    url: config ? readStringProperty(config, "url") : undefined,
    method: config ? readStringProperty(config, "method") : undefined,
  };
}

export function authDebug(event: string, details: AuthDebugDetails = {}): void {
  writeAndPrintAuthDebug("debug", event, details);
}

export function authDebugWarn(event: string, details: AuthDebugDetails = {}): void {
  writeAndPrintAuthDebug("warn", event, details);
}

export function installAuthDebugHelpers(): void {
  if (typeof window === "undefined") {
    return;
  }

  Reflect.set(window, "dumpAuthDebugLog", () => readAuthDebugLog());
  Reflect.set(window, "clearAuthDebugLog", () => {
    window.localStorage.removeItem(AUTH_DEBUG_LOG_KEY);
  });
}
